import { useEffect, useState } from "react";
import { Alert, Flex, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";

import { useStores } from "stores";
import { EditableFile, FileInput, WhiteBoxBlock } from "ui";
import { ButtonDownload } from "components/ButtonDownload";
import { ENDPOINTS, removeCompanyOfferFile, uploadCompanyOfferFile } from "services";
import { FILE_TYPE_ENUM, NOTIFICATION_TYPE_ENUM, COMPANY_STATUS_ENUM, DocUploadedResponse } from "interfaces";
import { useNotificationContext } from "providers";
import { fileExistsInObject, parseErrorMessage } from "utils";
import { getStatusMessage } from "services";

import styles from "./styles.module.css";

export const ComplianceReviewPage = observer(() => {
  const { user, companyCard, settings } = useStores();
  const { openNotification } = useNotificationContext();

  const [loading, setLoading] = useState(false);

  const userCompanyId = user.userInfo?.company_id;
  const { documents } = companyCard.card;

  const { isCompanyDocUploading } = user.roles;

  const logoutIfActive = () => {
    openNotification({
      message: "Компания активирована, авторизируйтесь в системе повторно",
      type: NOTIFICATION_TYPE_ENUM.SUCCESS,
    });
    user.logout();
  };

  useEffect(() => {
    if (userCompanyId) {
      const fetchCardInterval = setInterval(() => {
        companyCard.updateCardById(userCompanyId).then(() => {
          if (companyCard.card.status === COMPANY_STATUS_ENUM.ACTIVE) {
            logoutIfActive();
          }
        });
      }, 30000);

      settings.fetchRuleFiles();

      companyCard.fetchCompanyCardById(userCompanyId).then(() => {
        if (companyCard.card.status === COMPANY_STATUS_ENUM.ACTIVE) {
          logoutIfActive();
        }
      });

      return () => clearInterval(fetchCardInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCard, userCompanyId, user]);

  const fileOnFinishHandler = () => {
    if (!userCompanyId) return;
    setTimeout(() => {
      companyCard.fetchCompanyCardById(userCompanyId);
      setLoading(false);
    }, 1500);
  };

  const handleUploadFile = async (responseUpload: DocUploadedResponse[]) => {
    if (!userCompanyId) return;
    setLoading(true);
    await uploadCompanyOfferFile(
      { id: userCompanyId },
      { docIds: responseUpload.map((item) => item.id) }
    )
      .then(() => {
        fileOnFinishHandler();
        openNotification({
          message: "Оферта загружена, оператор активирует профиль после проверки",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
      })
      .catch((e) => {
        setLoading(false);
        throw e;
      });
  };

  const handleRemoveFile = async (fileId: string) => {
    if (!userCompanyId) return;
    setLoading(true);
    return await removeCompanyOfferFile({
      id: userCompanyId,
      documentId: fileId,
    })
      .then(() => {
        fileOnFinishHandler();
      })
      .catch((e) => {
        setLoading(false);
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      });
  };

  const canUploadOfferFile = isCompanyDocUploading;

  return (
    <Spin spinning={loading || companyCard.isPending}>
      <Flex vertical justify="center" className={styles.compliancePage}>
        <Typography.Title level={4}>Ознакомьтесь с правилами платформы и подпишите оферту</Typography.Title>
        <Flex vertical gap="middle">
          <WhiteBoxBlock>
            <Typography.Text strong id="compliance-review-documents">
              Файлы для скачивания
            </Typography.Text>
            <Flex vertical gap="middle" className="padding-vertical-md">
              {settings.ruleFiles.length > 0 &&
                settings.ruleFiles.map((doc) => (
                  <EditableFile
                    file={{
                      url: doc.url,
                      docName: doc.docName,
                      fileSize: doc.fileSize,
                      uploadedAt: doc.uploadedAt,
                    }}
                    tooltip={"Правила платформы"}
                    key={doc.id}
                  />
                ))}
              {companyCard.fetchStatus === "success" && (
                <>
                  {fileExistsInObject(documents, FILE_TYPE_ENUM.OFFER_SIGNED) && canUploadOfferFile ? (
                    <Flex vertical gap="middle">
                      {documents[FILE_TYPE_ENUM.OFFER_SIGNED].map((item) => (
                        <EditableFile
                          file={item}
                          removeFileHandler={() => handleRemoveFile(item.id)}
                          tooltip="Оферта"
                          showRemoveIcon
                          key={item.id}
                        />
                      ))}
                      <Alert description="Ожидайте одобрения организации" />
                    </Flex>
                  ) : (
                    <ButtonDownload
                      endpoint={ENDPOINTS.companyOfferFile()}
                      fileNameStatic="Оферта.pdf"
                      tooltip="Оферта.pdf"
                    />
                  )}
                </>
              )}
            </Flex>
          </WhiteBoxBlock>
          {companyCard.fetchStatus === "success" && canUploadOfferFile && (
            <>
              <WhiteBoxBlock>
                <Typography.Text strong id="compliance-review-new-documents">
                  Подпишите и загрузите файл оферты
                </Typography.Text>
                <FileInput
                  fileType={FILE_TYPE_ENUM.OFFER_SIGNED}
                  onUpload={handleUploadFile}
                  blockTitle="Документ (скан договора)"
                  className="margin-top-md"
                  multiple
                />
              </WhiteBoxBlock>
            </>
          )}
        </Flex>
      </Flex>
      {companyCard.fetchStatus === "error" && companyCard.fetchError && (
        <Flex className={styles.compliancePage}>
          <Alert
            message={getStatusMessage(companyCard.fetchError.status)}
            description={parseErrorMessage(companyCard.fetchError)}
            type="error"
            showIcon
            className="full-width margin-top-md"
          />
        </Flex>
      )}
    </Spin>
  );
});
